import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type SettingsTracker = {
  profileUpdated: (params: {
    language?: string | null;
  }) => void;

  passwordChanged: () => void;

  languageChanged: (params: {
    language: string;
    previousLanguage?: string | null;
  }) => void;

  userInvited: (params: {
    role?: string | null;
  }) => void;

  userRoleUpdated: (params: {
    userId: string;
    newRole?: string | null;
  }) => void;

  userDeleted: (params: {
    userId: string;
  }) => void;

  workspaceSwitched: (params: {
    workspaceId: string;
  }) => void;

  workspaceCreated: (params: {
    workspaceName?: string | null;
  }) => void;
};

export const useSettingsTracking = (): SettingsTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'settings',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<SettingsTracker>(() => ({
    profileUpdated: (params) => track('settings_profile_updated', params),
    passwordChanged: () => track('settings_password_changed', {}),
    languageChanged: (params) => track('settings_language_changed', params),
    userInvited: (params) => track('settings_user_invited', params),
    userRoleUpdated: (params) => track('settings_user_role_updated', params),
    userDeleted: (params) => track('settings_user_deleted', params),
    workspaceSwitched: (params) => track('settings_workspace_switched', params),
    workspaceCreated: (params) => track('settings_workspace_created', params),
  }), [track]);
};
